<template>
    <div v-if="!value && !dataUri" class="custom-file">
        <input ref="input" type="file" class="custom-file-input" :id="id" accept="image/*" @change="onPhotoSelected">
        <label class="custom-file-label" :for="id">
            {{file ? file.name : $t("select_file")}}
            <button type="button">{{ $t("browse") }}</button>
        </label>
        <s-image-cropper ref="imageCropper" />
    </div>
    <div v-else class="image-adjusted-container">
        <div class="image-adjusted">
            <img :src="value || dataUri">
            <div v-if="!value" class="uploading">
                <font-awesome-icon pulse :icon="['fal', 'spinner']"/>
            </div>
        </div>
        <button v-if="value" type="button" class="btn btn-sm btn-outline-secondary" @click="reset">{{ $t('image_remove_btn') }}</button>
    </div>
</template>

<style scoped>
    .image-adjusted {
        position: relative;
        padding: 4px;
        border: 1px solid #ced4da;
        border-radius: 4px;
    }

    .image-adjusted img {
        max-width: 100%;
        height: 100px;
    }

    .image-adjusted-container {
      display: flex;
      flex-direction: column;
      align-items: flex-start;
    }

    .image-adjusted-container .btn {
        margin-top: 0.25rem;
    }

    .uploading {
        position: absolute;
        left: 0;
        top: 0;
        width: 100%;
        height: 100%;
        display: flex;
        align-items: center;
        justify-content: center;
        background: rgba(255, 255, 255, 0.8);
        border-radius: 4px;
    }

    .uploading svg {
        display: inline-block;
        width: 45px;
        max-width: 70%;
        height: 45px;
        max-height: 70%;
        color: #777;
    }
</style>

<script>
    import axios from "axios";
    import {SIGNED_URL} from "@/gql/Storage";
    import SImageCropper from "../general/SImageCropper";

    export default {
        name: "input-image",
        i18nOptions: {namespaces: ['input_image']},
        components: {SImageCropper},
        props: {
            id: {
                type: String,
                default: "input-image"
            },
            value: String,
            width: {
                type: Number,
                required: true
            },
            height: {
                type: Number,
                required: true
            },
            format: {
                type: String,
                default: "jpeg",
                validator: v => ['webp', 'jpeg', 'png'].indexOf(v) !== -1
            },
            quality: {
                type: Number,
                default: 0.75,
                validator: v => v > 0 && v <= 1
            },
            thumbs: {
                type: Array,
                validator: a => a.every(o => o.width && o.height)
            }
        },
        data: () => ({
            file: null,
            dataUri: null,
            uploadPercentage: 0,
            mimeTypes: {
                webp: "image/webp",
                jpeg: "image/jpeg",
                png: "image/png",
            },
            waitForUploadPromise: null
        }),
        methods: {
            onPhotoSelected(event) {
                this.dataUri = null
                if (event.target.files.length) {
                    this.file = event.target.files[0]
                    const reader = new FileReader()
                    reader.onload = () => {
                        this.waitForUploadPromise = this.$refs.imageCropper.crop(reader.result, {
                            width: this.width,
                            height: this.height,
                            format: this.format,
                            quality: this.quality,
                            thumbs: this.thumbs
                        }).then(imagesDataUri => {
                            this.dataUri = imagesDataUri[0]
                            this.file = null
                            if (this.$refs.input) {
                                this.$refs.input.value = ""
                            }
                            this.uploadPercentage = 0
                            this.$emit("uploadStart")
                            return Promise.all(imagesDataUri.map((dataUri, index) => this.uploadPhoto(dataUri, index > 0 ? "thumb-" + index : null)))
                        }).then(tempUrls => {
                            this.$emit("thumbsReady", tempUrls.slice(1))
                            this.$emit("input", tempUrls[0])
                            this.$emit("uploadEnd")
                        }).catch(() => {
                            this.file = null
                            if (this.$refs.input) {
                                this.$refs.input.value = ""
                            }
                        });
                    }
                    reader.readAsDataURL(event.target.files[0]);
                } else {
                    this.file = null;
                }
            },
            async uploadPhoto(imageDataUri, urlPart) {
                const blob = this.dataUriToBlob(imageDataUri)
                const signedUrlResponse = await this.$apolloClient.query({
                    query: SIGNED_URL,
                    variables: {
                        extension: this.format,
                        size: blob.size,
                        urlPart: urlPart
                    },
                    fetchPolicy: "network-only",
                })
                const signedUrl = signedUrlResponse.data.signedUrl
                await axios.put(signedUrl, blob, {
                    headers: {'Content-Type': this.mimeTypes[this.format]},
                    onUploadProgress: function( progressEvent ) {
                        this.uploadPercentage = Math.round((progressEvent.loaded / progressEvent.total) * 100)
                    }.bind(this)
                })
                return signedUrl.split("?")[0]
            },
            dataUriToBlob(dataUri) {
                const byteString = atob(dataUri.split(',')[1]);
                const mimeString = dataUri.split(',')[0].split(':')[1].split(';')[0]
                const ab = new ArrayBuffer(byteString.length);
                const ia = new Uint8Array(ab);
                for (let i = 0; i < byteString.length; i++) {
                    ia[i] = byteString.charCodeAt(i);
                }
                return new Blob([ab], {type: mimeString});
            },
            reset() {
                this.dataUri = null
                this.uploadPercentage = 0
                this.$emit("reset")
                this.$emit('input', null)
            },
            waitForUpload() {
                if(this.dataUri && !this.value && this.waitForUploadPromise) {
                    return this.waitForUploadPromise
                } else {
                    return Promise.resolve()
                }
            }
        }
    }
</script>
